import React from "react";
import { css } from "@linaria/core";
import HighchartsComponent from "@ui/Highcharts";
import type { Options } from "@ui/Highcharts";
import Highcharts from "highcharts";
import { postLatestAnalysisPlotBandColor } from "../../Chart/hooks/usePostLatestAnalysisPlotBand";

const EventTableSparkline = ({
  options,
  latestAnalysisDate,
}: {
  options: Options;
  latestAnalysisDate: number | null;
}) => {
  const chartRef = React.useRef<Highcharts.Chart>(null);
  const [overlayStyle, setOverlayStyle] = React.useState<{
    left: string;
    right: string;
    backgroundColor: string;
  } | null>(null);

  React.useLayoutEffect(() => {
    const chart = chartRef.current;
    if (!chart) return;

    const updateOverlayStyle = () => {
      const xAxis = chart.xAxis[0];
      if (!xAxis || latestAnalysisDate === null) {
        setOverlayStyle(null);
        return;
      }

      const { min, max } = xAxis.getExtremes();
      const hasBounds = typeof min === "number" && typeof max === "number";
      if (!hasBounds || latestAnalysisDate >= max) {
        setOverlayStyle(null);
        return;
      }

      const left =
        latestAnalysisDate <= min ? chart.plotLeft : xAxis.toPixels(latestAnalysisDate, false);
      const right = chart.chartWidth - (chart.plotLeft + chart.plotWidth);

      setOverlayStyle({
        left: `${left}px`,
        right: `${right}px`,
        backgroundColor: postLatestAnalysisPlotBandColor,
      });
    };

    const handleRedraw: Highcharts.EventCallbackFunction<Highcharts.Chart> = () => {
      updateOverlayStyle();
    };

    updateOverlayStyle();
    Highcharts.addEvent(chart, "redraw", handleRedraw);
    return () => Highcharts.removeEvent(chart, "redraw", handleRedraw);
  }, [latestAnalysisDate]);

  return (
    <div className={wrapper}>
      {overlayStyle === null ? null : <div className={overlay} style={overlayStyle} />}
      <div className={chart}>
        <HighchartsComponent
          name="Event sparkline"
          ref={chartRef}
          options={options}
          selectablePoints={false}
        />
      </div>
    </div>
  );
};

export default React.memo(EventTableSparkline);

const wrapper = css`
  position: relative;
  height: 100%;
  min-height: 100%;
`;

const chart = css`
  position: relative;
  z-index: 2;
  height: 100%;
`;

const overlay = css`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
  z-index: 1;
`;
